

// components/EchoComposer/EchoComposer.js
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Popper,
  Card,
  CircularProgress,
  Typography,
  Chip,
  Divider,
  Avatar
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  ShoppingCart as ShoppingCartIcon,
  Inventory as InventoryIcon,
  RequestQuote as RequestQuoteIcon,
  Category as CategoryIcon,
  Gavel as GavelIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDUserAvatar from 'components/MDUserAvatar';

// Comment out other entity types, keep only User
const ENTITY_TYPES = [
  { type: 'User', label: 'Users', icon: <PersonIcon />, color: 'primary', endpoint: '/users/' },
  // { type: 'Lead', label: 'Leads', icon: <PeopleIcon />, color: 'success', endpoint: '/leads/' },
  // { type: 'Deal', label: 'Deals', icon: <BusinessIcon />, color: 'info', endpoint: '/deals/' },
  // { type: 'Account', label: 'Accounts', icon: <BusinessIcon />, color: 'warning', endpoint: '/accounts/' },
  // { type: 'Contact', label: 'Contacts', icon: <PeopleIcon />, color: 'error', endpoint: '/contacts/' },
  // { type: 'Order', label: 'Orders', icon: <ShoppingCartIcon />, color: 'secondary', endpoint: '/orders/' },
  // { type: 'Invoice', label: 'Invoices', icon: <ReceiptIcon />, color: 'primary', endpoint: '/invoices/' },
  // { type: 'Quote', label: 'Quotations', icon: <RequestQuoteIcon />, color: 'success', endpoint: '/quotations/' },
  // { type: 'Product', label: 'Products', icon: <CategoryIcon />, color: 'info', endpoint: '/products/' },
  // { type: 'Case', label: 'Cases', icon: <GavelIcon />, color: 'error', endpoint: '/cases/' }
];

const EchoComposer = ({ 
  onSubmit, 
  placeholder = "Type @ to mention users...", 
  axiosInstance,
  objectType = null, // e.g., 'lead', 'deal', etc.
  objectId = null,    // ID of the entity this message relates to
  feedItemId = null,  // ID of the feed item being edited (for edit mode)
  onSuccess, // Add callback for success
  onError,   // Add callback for error
  onCancel,  // Add callback for cancel/close modal
  value,     // Controlled value for edit mode
  onChange,  // Controlled onChange for edit mode
  isEditMode = false, // Whether this is in edit mode
  sx = {} // Custom styling
}) => {
  const [content, setContent] = useState(value || '');
  const [originalContent, setOriginalContent] = useState(value || ''); // Store original content for backend
  const [showMentionMenu, setShowMentionMenu] = useState(false);
  const [mentionAnchor, setMentionAnchor] = useState(null);
  const [selectedEntityType, setSelectedEntityType] = useState(null);
  const [entityItems, setEntityItems] = useState([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentions, setMentions] = useState([]);
  const [mentionStartPosition, setMentionStartPosition] = useState(0);
  const textFieldRef = useRef(null);
  const inputRef = useRef(null);
  const popperRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [validationTimeout, setValidationTimeout] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  // Update content when value prop changes (for controlled mode)
  useEffect(() => {
    if (value !== undefined) {
      setContent(value);
      setOriginalContent(value); // Store original content
      
      // If in edit mode, parse existing mentions from the content
      if (isEditMode && value) {
        parseExistingMentions(value);
      }
    }
  }, [value, isEditMode]);

  // Debug logging for props
  useEffect(() => {
    if (isEditMode) {
      //console.log('=== ECHO COMPOSER PROPS DEBUG ===');
      //console.log('isEditMode:', isEditMode);
      //console.log('objectType:', objectType);
      //console.log('objectId:', objectId);
      //console.log('feedItemId:', feedItemId);
      //console.log('value:', value);
    }
  }, [isEditMode, objectType, objectId, feedItemId, value]);

  // Parse existing mentions from content (for edit mode)
  const parseExistingMentions = async (contentText) => {
    if (!contentText || !axiosInstance) return;
    
    try {
      // Get all users to match against
      const response = await axiosInstance.get('/users/');
      const allUsers = response.data || [];
      
      // Find all @username patterns in the content
      // Match @ followed by a complete username (can contain @ for email-style usernames)
      // Use negative lookahead to ensure we don't match partial usernames
      const mentionRegex = /@([a-zA-Z0-9._-]+(?:@[a-zA-Z0-9._-]+)?)(?=\s|$)/g;
      const matches = [];
      let match;
      
      // First pass: collect all matches and their positions
      const allMatches = [];
      while ((match = mentionRegex.exec(contentText)) !== null) {
        allMatches.push({
          fullMatch: match[0],
          username: match[1],
          startIndex: match.index,
          endIndex: match.index + match[0].length
        });
      }
      
      // Sort matches by start index to process them in order
      allMatches.sort((a, b) => a.startIndex - b.startIndex);
      
      // Second pass: build the new content and collect valid mentions
      let newContent = contentText;
      let offset = 0; // Track position changes due to replacements
      
      allMatches.forEach((matchInfo) => {
        const user = allUsers.find(u => u.username === matchInfo.username);
        
        if (user) {
          const entityName = formatEntityName(user, 'User');
          
          // Calculate adjusted position after previous replacements
          const adjustedStartIndex = matchInfo.startIndex + offset;
          
          // Replace the username with entity name
          const beforeReplacement = newContent.substring(0, adjustedStartIndex);
          const afterReplacement = newContent.substring(adjustedStartIndex + matchInfo.fullMatch.length);
          newContent = beforeReplacement + entityName + afterReplacement;
          
          // Update offset for next replacements
          offset += entityName.length - matchInfo.fullMatch.length;
          
          // Add to mentions list
          matches.push({
            id: user.id,
            type: 'User',
            name: entityName,
            position: adjustedStartIndex,
            raw: user,
            username: user.username,
            displayName: entityName
          });
        }
      });
      
      // Update mentions state with existing mentions
      if (matches.length > 0) {
        setMentions(matches);
        // Update content to show entity names instead of usernames (for display only)
        setContent(newContent);
        // Keep original content unchanged for backend submission
        setOriginalContent(contentText);
      }
    } catch (error) {
      console.error('Error parsing existing mentions:', error);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeout) {
        clearTimeout(validationTimeout);
      }
    };
  }, [validationTimeout]);

  // Debug logging for mentions and content
  useEffect(() => {
    if (isEditMode && mentions.length > 0) {
      //console.log('Edit mode - Current mentions:', mentions);
      //console.log('Edit mode - Current content:', content);
    }
  }, [mentions, content, isEditMode]);

  // Reset component state when switching modes
  useEffect(() => {
    if (!isEditMode) {
      // Reset mentions when switching to new message mode
      setMentions([]);
      setValidationError('');
    }
  }, [isEditMode]);

  // Validate mentions in real-time
  // Validate mentions in real-time
const validateMentionsRealTime = async (text) => {
  try {
    const response = await axiosInstance.get("/users/");
    const allUsers = response.data || [];
    const validUsernames = [...allUsers.map((user) => user.username)];

    // Match all @mentions in the text
    const mentionRegex = /@(\w+)/g;
    const matches = [...text.matchAll(mentionRegex)].map((m) => m[1]);

    if (matches.length === 0) {
      setValidationError(""); // no mentions, clear error
      return;
    }

    // Check each mention
    const invalidUsers = matches.filter((mention) => {
      // If mention partially matches any valid username, ignore error
      const partialMatch = validUsernames.some((username) =>
        username.startsWith(mention)
      );
      return !partialMatch;
    });

    if (invalidUsers.length > 0) {
      setValidationError(`Invalid username(s): ${invalidUsers.join(", ")}`);
    } else {
      setValidationError(""); // all mentions valid or partial
    }
  } catch (err) {
    console.error("Error validating mentions:", err);
    setValidationError("Error validating mentions");
  }
};


  // Update mention positions when content changes
  const updateMentionPositions = (oldContent, newContent) => {
    if (!mentions.length) return;
    
    const updatedMentions = mentions.map(mention => {
      if (mention.displayName) {
        // Find the new position of this mention in the updated content
        const newPosition = newContent.indexOf(mention.displayName);
        if (newPosition !== -1) {
          return { ...mention, position: newPosition };
        }
      }
      return mention;
    }).filter(mention => mention.position !== -1); // Remove mentions that are no longer in content
    
    if (updatedMentions.length !== mentions.length) {
      setMentions(updatedMentions);
    }
  };

  // Handle content changes
  const handleContentChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    const oldContent = content;
    
    // Update internal state
    setContent(newValue);
    
    // Call onChange if provided (controlled mode)
    if (onChange) {
      onChange(newValue);
    }

    // Update mention positions
    updateMentionPositions(oldContent, newValue);

    // Validate mentions in real-time with debounce
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }
    
    const timeout = setTimeout(() => {
      validateMentionsRealTime(newValue);
    }, 500); // Debounce for 500ms
    
    setValidationTimeout(timeout);

    // Check if @ was typed
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1 && lastAtIndex === cursorPos - 1) {
      // Just typed @ - directly show users
      setShowMentionMenu(true);
      setMentionAnchor(textFieldRef.current);
      setSelectedEntityType(ENTITY_TYPES[0]); // Set to User type directly
      setMentionSearch('');
      setMentionStartPosition(lastAtIndex);
      // Automatically fetch users
      handleEntityTypeSelect(ENTITY_TYPES[0]);
    } else if (showMentionMenu && lastAtIndex >= mentionStartPosition) {
      // Typing after @
      const searchTerm = textBeforeCursor.substring(lastAtIndex + 1);
      setMentionSearch(searchTerm);
    } else if (showMentionMenu) {
      // @ was deleted or cursor moved away
      setShowMentionMenu(false);
      setSelectedEntityType(null);
    }
    
    // Clean up mentions that are no longer in the content
    const updatedMentions = mentions.filter(mention => {
      if (mention.displayName) {
        return newValue.includes(mention.displayName);
      }
      return true;
    });
    
    if (updatedMentions.length !== mentions.length) {
      setMentions(updatedMentions);
    }
  };

  // Format entity name based on type
  const formatEntityName = (entity, type) => {
    if (!entity) return '';
    if (!type) return entity.fullname || `${entity.first_name || ''} ${entity.last_name || ''}`.trim() || entity.username || `ID: ${entity.id}`;
    
    switch (type.toLowerCase()) {
      case 'user':
        return entity.fullname || `${entity.first_name || ''} ${entity.last_name || ''}`.trim() || entity.username || `ID: ${entity.id}`;
      // Comment out other entity types
      // case 'account':
      //   return entity.name || '';
      // case 'lead':
      //   return `${entity.first_name || ''} ${entity.last_name || ''} ${entity.company ? `- ${entity.company}` : ''}`.trim();
      // case 'deal':
      //   return `${entity.name || ''} ${entity.amount ? `- $${entity.amount}` : ''}`.trim();
      // case 'contact':
      //   return `${entity.first_name || ''} ${entity.last_name || ''} ${entity.email ? `- ${entity.email}` : ''}`.trim();
      // case 'case':
      //   return `${entity.case_number || ''} ${entity.subject ? `- ${entity.subject}` : ''}`.trim();
      // case 'order':
      //   return `${entity.order_number || ''} ${entity.total_amount ? `- $${entity.total_amount}` : ''}`.trim();
      // case 'invoice':
      //   return `Invoice #${entity.invoice_number || entity.id} ${entity.total_amount ? `- $${entity.total_amount}` : ''}`.trim();
      // case 'quote':
      //   return `${entity.quote_name || ''} ${entity.total_amount ? `- $${entity.total_amount}` : ''}`.trim();
      // case 'product':
      //   return `${entity.name || ''} ${entity.price ? `- $${entity.price}` : ''}`.trim();
      default:
        return entity.name || entity.title || `ID: ${entity.id}`;
    }
  };

  // Handle entity type selection
  const handleEntityTypeSelect = async (entityType) => {
    setSelectedEntityType(entityType);
    setIsLoadingItems(true);
    setMentionSearch('');
    
    try {
      // Fetch items based on entity type
      const response = await axiosInstance.get(entityType.endpoint);
      setEntityItems(response.data || []);
    } catch (error) {
      console.error(`Error fetching ${entityType.type}s:`, error);
      setEntityItems([]);
    } finally {
      setIsLoadingItems(false);
    }
  };

  // Handle item selection
  const handleItemSelect = (item) => {
    // Get the current text before and after the mention
    const beforeMention = content.substring(0, mentionStartPosition);
    const currentCursorPos = inputRef.current?.selectionStart || content.length;
    const afterCursor = content.substring(currentCursorPos);
    
    // Format the mention text - use username for backend parsing but display full name
    let mentionText;
    let displayText;
    if (selectedEntityType?.type === 'User') {
      // For users, use username for backend parsing but display full name
      // Format: @username (this will be parsed by backend and replaced with full name)
      mentionText = `@${item.username}`;
      // Display the full name without @ for better UX
      displayText = formatEntityName(item, selectedEntityType?.type);
    } else {
      // For other entities, use the format: @EntityType:entityId (commented out for now)
      // mentionText = `@${selectedEntityType?.type}:${item.id}`;
      mentionText = `@${item.username}`; // Default to username for now
      displayText = formatEntityName(item, selectedEntityType?.type);
    }
    
    // Update content with display text (what user sees)
    const newContent = beforeMention + displayText + ' ' + afterCursor;
    setContent(newContent);
    
    // Call onChange if provided (controlled mode)
    if (onChange) {
      onChange(newContent);
    }
    
    // Add to mentions list
    const itemName = formatEntityName(item, selectedEntityType?.type);
    setMentions([...mentions, {
      id: item.id,
      type: selectedEntityType?.type,
      name: itemName,
      position: mentionStartPosition,
      raw: item,
      username: item.username, // Store username for backend
      displayName: displayText // Store display name for UI
    }]);
    
    // Close menu
    setShowMentionMenu(false);
    setSelectedEntityType(null);
    setMentionSearch('');
    
    // Focus back to text field and set cursor position
    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPos = beforeMention.length + displayText.length + 1;
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  // Validate mentioned users exist
  const validateMentions = async () => {
    // Check mentions from the mentions state
    if (mentions.length === 0) {
      return { isValid: true, invalidUsers: [] };
    }
    
    try {
      // Get all users to validate against
      const response = await axiosInstance.get('/users/');
      const allUsers = response.data || [];
      const validUsernames = allUsers.map(user => user.username);
      
      const invalidUsers = mentions
        .filter(mention => mention.username)
        .filter(mention => !validUsernames.includes(mention.username))
        .map(mention => mention.username);
      
      return {
        isValid: invalidUsers.length === 0,
        invalidUsers
      };
    } catch (error) {
      console.error('Error validating mentions:', error);
      // If we can't validate, allow the post (backend will handle it)
      return { isValid: true, invalidUsers: [] };
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    //console.log('=== HANDLE SUBMIT CALLED ===');
    //console.log('isEditMode:', isEditMode);
    //console.log('content:', content);
    //console.log('mentions:', mentions);
    //console.log('isSubmitting:', isSubmitting);
    //console.log('validationError:', validationError);
    
    if (content.trim() && !isSubmitting) {
      //console.log('=== SUBMISSION CONDITIONS MET ===');
      setIsSubmitting(true);
      setValidationError('');
      
      try {
        let backendContent;
        
        //console.log('=== STARTING CONTENT PROCESSING ===');
        
        if (isEditMode) {
          //console.log('=== TAKING EDIT MODE PATH ===');
          //console.log('Edit mode props - objectId:', objectId);
          //console.log('Edit mode props - objectType:', objectType);
          //console.log('Edit mode props - value:', value);
          
          // For edit mode, process mentions the same way as new message mode
          // but then call onSubmit with the processed content for the parent to handle
          backendContent = content;
          
          //console.log('=== EDIT MODE LOGS ===');
          //console.log('1. Initial content:', content);
          //console.log('2. Initial mentions:', mentions);
          //console.log('3. Starting conversion process (same as new message)...');
          
          // Sort mentions by position in reverse order (SAME as new message mode)
          const sortedMentions = [...mentions].sort((a, b) => b.position - a.position);
          //console.log('4. Sorted mentions (reverse order):', sortedMentions);
          
          // Replace each entity name with @username format (SAME as new message mode)
          sortedMentions.forEach((mention, index) => {
            if (mention.username && mention.displayName) {
              //console.log(`5. Processing mention ${index + 1}:`);
              //console.log(`   - Username: ${mention.username}`);
              //console.log(`   - Display Name: ${mention.displayName}`);
              //console.log(`   - Position: ${mention.position}`);
              //console.log(`   - Current backendContent: "${backendContent}"`);
              
              // Escape special regex characters in the display name (SAME as new message mode)
              const escapedDisplayName = mention.displayName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              //console.log(`   - Escaped display name: "${escapedDisplayName}"`);
              
              // Use a more robust replacement that handles the exact text (SAME as new message mode)
              const regex = new RegExp(`(${escapedDisplayName})`, 'g');
              //console.log(`   - Regex created: /(${escapedDisplayName})/g`);
              
              const beforeReplacement = backendContent;
              backendContent = backendContent.replace(regex, `@${mention.username}`);
              //console.log(`   - Before replacement: "${beforeReplacement}"`);
              //console.log(`   - After replacement: "${backendContent}"`);
              //console.log(`   - Replacement successful: ${beforeReplacement !== backendContent}`);
            }
          });
          
          //console.log('6. Final backend content after all replacements:', backendContent);
          //console.log('7. Mentions array for backend:', mentions);
          //console.log('=== END EDIT MODE LOGS ===');
          
          // For edit mode, call onSubmit with the processed content
          // The parent component will handle the actual API call (PUT request)
          //console.log('8. Edit mode - Calling onSubmit with processed content');
          
          // Include the feed item ID in the edit data so the parent component can make the correct PUT request
          const editData = {
            body: backendContent, // The processed content with @username format (use 'body' to match backend schema)
            mentions: mentions.map(mention => ({
              type: mention.type,
              entity_id: mention.username,
              entity_name: mention.displayName
            })),
            object_type: objectType, // Use snake_case to match backend schema
            object_id: objectId,
            feed_item_id: feedItemId // Include the feed item ID for the PUT request
          };
          
          //console.log('9. Edit mode - Data being sent to onSubmit:', editData);
          //console.log('   - Body:', editData.body);
          //console.log('   - Mentions:', editData.mentions);
          //console.log('   - Object Type:', editData.object_type);
          //console.log('   - Object ID:', editData.object_id);
          //console.log('   - Feed Item ID:', editData.feed_item_id);
          //console.log('   - Feed Item ID prop received:', feedItemId);
          //console.log('   - Note: Parent component will use feed_item_id for PUT request');
          
          await onSubmit(editData);
          
          //console.log('9. Edit mode - onSubmit callback completed');
          
          // Call success callback if provided
          if (onSuccess) {
            onSuccess('Message updated successfully!');
          }
        } else {
          //console.log('=== TAKING NEW MESSAGE MODE PATH ===');
          // For new message mode, convert display names back to @username format
          backendContent = content;
          
          //console.log('=== NEW MESSAGE MODE LOGS ===');
          //console.log('1. Initial content:', content);
          //console.log('2. Initial mentions:', mentions);
          //console.log('3. Starting conversion process...');
          
          // Sort mentions by position in reverse order to avoid position conflicts during replacement
          const sortedMentions = [...mentions].sort((a, b) => b.position - a.position);
          //console.log('4. Sorted mentions (reverse order):', sortedMentions);
          
          // Replace each display name with @username format, starting from the end
          sortedMentions.forEach((mention, index) => {
            if (mention.username && mention.displayName) {
              //console.log(`5. Processing mention ${index + 1}:`);
              //console.log(`   - Username: ${mention.username}`);
              //console.log(`   - Display Name: ${mention.displayName}`);
              //console.log(`   - Position: ${mention.position}`);
              //console.log(`   - Current backendContent: "${backendContent}"`);
              
              // Escape special regex characters in the display name
              const escapedDisplayName = mention.displayName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              //console.log(`   - Escaped display name: "${escapedDisplayName}"`);
              
              // Use a more robust replacement that handles the exact text
              const regex = new RegExp(`(${escapedDisplayName})`, 'g');
              //console.log(`   - Regex created: /(${escapedDisplayName})/g`);
              
              const beforeReplacement = backendContent;
              backendContent = backendContent.replace(regex, `@${mention.username}`);
              //console.log(`   - Before replacement: "${beforeReplacement}"`);
              //console.log(`   - After replacement: "${backendContent}"`);
              //console.log(`   - Replacement successful: ${beforeReplacement !== backendContent}`);
            }
          });
          
          //console.log('6. Final backend content after all replacements:', backendContent);
          //console.log('7. Mentions array for backend:', mentions);
          //console.log('=== END NEW MESSAGE MODE LOGS ===');
        }
        
        // Validate mentions before posting
        const validation = await validateMentions();
        
        //console.log('=== VALIDATION RESULTS ===');
        //console.log('Validation result:', validation);
        
        if (!validation.isValid) {
          //console.log('=== VALIDATION FAILED ===');
          const userList = validation.invalidUsers.join(', ');
          setValidationError(`The following users do not exist: ${userList}. Please remove these mentions before posting.`);
          setIsSubmitting(false);
          return;
        }
        
        //console.log('=== VALIDATION PASSED ===');
        
        if (isEditMode) {
          //console.log('=== TAKING EDIT MODE PATH ===');
          // For edit mode, process mentions the same way as new message mode
          // but then call onSubmit with the processed content for the parent to handle
          backendContent = content;
          
          //console.log('=== EDIT MODE LOGS ===');
          //console.log('1. Initial content:', content);
          //console.log('2. Initial mentions:', mentions);
          //console.log('3. Starting conversion process (same as new message)...');
          
          // Sort mentions by position in reverse order (SAME as new message mode)
          const sortedMentions = [...mentions].sort((a, b) => b.position - a.position);
          //console.log('4. Sorted mentions (reverse order):', sortedMentions);
          
          // Replace each entity name with @username format (SAME as new message mode)
          sortedMentions.forEach((mention, index) => {
            if (mention.username && mention.displayName) {
              //console.log(`5. Processing mention ${index + 1}:`);
              //console.log(`   - Username: ${mention.username}`);
              //console.log(`   - Display Name: ${mention.displayName}`);
              //console.log(`   - Position: ${mention.position}`);
              //console.log(`   - Current backendContent: "${backendContent}"`);
              
              // Escape special regex characters in the display name (SAME as new message mode)
              const escapedDisplayName = mention.displayName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              //console.log(`   - Escaped display name: "${escapedDisplayName}"`);
              
              // Use a more robust replacement that handles the exact text (SAME as new message mode)
              const regex = new RegExp(`(${escapedDisplayName})`, 'g');
              //console.log(`   - Regex created: /(${escapedDisplayName})/g`);
              
              const beforeReplacement = backendContent;
              backendContent = backendContent.replace(regex, `@${mention.username}`);
              //console.log(`   - Before replacement: "${beforeReplacement}"`);
              //console.log(`   - After replacement: "${backendContent}"`);
              //console.log(`   - Replacement successful: ${beforeReplacement !== backendContent}`);
            }
          });
          
          //console.log('6. Final backend content after all replacements:', backendContent);
          //console.log('7. Mentions array for backend:', mentions);
          //console.log('=== END EDIT MODE LOGS ===');
          
          // For edit mode, call onSubmit with the processed content
          // The parent component will handle the actual API call (PUT request)
          //console.log('8. Edit mode - Calling onSubmit with processed content');
          await onSubmit(backendContent);
          
          //console.log('9. Edit mode - onSubmit callback completed');
          
          // Call success callback if provided
          if (onSuccess) {
            onSuccess('Message updated successfully!');
          }
        } else {
          // For new message mode, create the payload matching your API structure
          const payload = {
            feeditem: {
              object_type: objectType,
              object_id: objectId,
              body: backendContent, // Use the converted content with @username format
              mentions: [] // Backend will parse mentions from body automatically
            }
          };

          //console.log('8. API Payload being sent:', payload);
          //console.log('9. Making API call to /feed-items/...');

          // Call the API
          const response = await axiosInstance.post('/feed-items/', payload);
          
          //console.log('10. API Response received:', response.data);
          //console.log('11. Response body field:', response.data.body);
          //console.log('12. Response mentions field:', response.data.mentions);
          
          // Call the onSubmit callback with the response
          await onSubmit(response.data);
          
          // Clear the form
          setContent('');
          setMentions([]);
          setValidationError('');
          
          // Call success callback if provided
          if (onSuccess) {
            onSuccess('Message posted successfully!');
          }
        }
      } catch (error) {
        console.error('Error posting message:', error);
        // Call error callback if provided
        if (onError) {
          onError(error.response?.data?.detail || 'Failed to post message');
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Filter entity types based on search - only show User type
  const filteredEntityTypes = mentionSearch 
    ? ENTITY_TYPES.filter(entity =>
        entity.label.toLowerCase().includes(mentionSearch.toLowerCase())
      )
    : ENTITY_TYPES;

  // Filter items based on search
  const filteredItems = entityItems.filter(item => {
    if (!mentionSearch) return true;
    const name = formatEntityName(item, selectedEntityType?.type);
    return name.toLowerCase().includes(mentionSearch.toLowerCase());
  });

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popperRef.current && 
        !popperRef.current.contains(event.target) &&
        textFieldRef.current &&
        !textFieldRef.current.contains(event.target)
      ) {
        setShowMentionMenu(false);
        setSelectedEntityType(null);
      }
    };

    if (showMentionMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMentionMenu]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (showMentionMenu && e.key === 'Escape') {
      e.preventDefault();
      setShowMentionMenu(false);
      setSelectedEntityType(null);
    }
  };

  // Parse and display content with mentions highlighted
  const parseContentForDisplay = () => {
    const parts = [];
    let lastIndex = 0;
    
    // Get list of invalid usernames for highlighting
    const invalidUsernames = validationError ? 
      validationError.match(/The following users do not exist: (.+?)\./)?.[1]?.split(', ') || [] : [];
    
    // Process mentions in order of their position
    const sortedMentions = [...mentions].sort((a, b) => a.position - b.position);
    
    sortedMentions.forEach((mention, index) => {
      // Add text before this mention
      if (mention.position > lastIndex) {
        parts.push({
          type: 'text',
          content: content.substring(lastIndex, mention.position)
        });
      }
      
      // Add the mention - always show displayName (entity name) in preview
      if (mention.username) {
        const isInvalid = invalidUsernames.includes(mention.username);
        const displayContent = mention.displayName || formatEntityName(mention.raw, mention.type);
        
        parts.push({
          type: isInvalid ? 'invalid-user-mention' : 'user-mention',
          content: displayContent,
          username: mention.username
        });
        
        // Update lastIndex based on the actual display content length
        lastIndex = mention.position + displayContent.length;
      } else {
        // Fallback for mentions without username
        lastIndex = mention.position + (mention.displayName || '').length;
      }
    });
    
    // Add remaining text after last mention
    if (lastIndex < content.length) {
      parts.push({
        type: 'text',
        content: content.substring(lastIndex)
      });
    }
    
    return parts;
  };

  // Validate that all mentions in content are tracked
  const validateMentionsTracking = () => {
    if (!content || !mentions.length) return;
    
    // Find all @username patterns in the current content
    const mentionRegex = /@([a-zA-Z0-9._-]+(?:@[a-zA-Z0-9._-]+)?)/g;
    const contentMentions = [];
    let match;
    
    while ((match = mentionRegex.exec(content)) !== null) {
      contentMentions.push(match[1]);
    }
    
    // Check if we have any untracked mentions
    const trackedUsernames = mentions.map(m => m.username);
    const untrackedMentions = contentMentions.filter(username => !trackedUsernames.includes(username));
    
    if (untrackedMentions.length > 0) {
      console.warn('Untracked mentions found:', untrackedMentions);
      // You could add logic here to handle untracked mentions
    }
  };

  // Call validation when content or mentions change
  useEffect(() => {
    if (isEditMode) {
      validateMentionsTracking();
    }
  }, [content, mentions, isEditMode]);

  return (
    <MDBox>
      <Card sx={{ p: 2, elevation: 0, boxShadow: 'none', ...sx }}>
        <TextField
          ref={textFieldRef}
          inputRef={inputRef}
          fullWidth
          multiline
          rows={3}
          value={content}
          onChange={handleContentChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '0.875rem',
              backgroundColor: 'white'
            },
            ...sx
          }}
        />
        
        {/* Validation Error */}
        {validationError && (
          <Box sx={{ mt: 1, p: 1.5, bgcolor: 'error.lighter', borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}>
            <Typography variant="body2" color="error.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="span" sx={{ fontSize: '1rem' }}></Box>
              {validationError}
            </Typography>
          </Box>
        )}
        
        {/* Validation Loading */}
        {isValidating && (
          <Box sx={{ mt: 1, p: 1.5, bgcolor: 'info.lighter', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
            <Typography variant="body2" color="info.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="info" />
              Validating mentions...
            </Typography>
          </Box>
        )}
        
        {/* Preview with highlighted mentions - show for both edit and new message modes */}
        {content && mentions.length > 0 && (
          <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {isEditMode ? 'Edit Preview:' : 'Preview:'}
            </Typography>
            <Typography variant="body2">
              {parseContentForDisplay().map((part, index) => {
                if (part.type === 'mention') {
                  return (
                    <Box
                      key={index}
                      component="span"
                      sx={{
                        color: 'info.main',
                        fontWeight: 'bold',
                        backgroundColor: 'info.lighter',
                        px: 0.5,
                        borderRadius: 0.5,
                        mx: 0.25
                      }}
                    >
                      {part.content}
                    </Box>
                  );
                } else if (part.type === 'user-mention') {
                  return (
                    <Box
                      key={index}
                      component="span"
                      sx={{
                        color: 'primary.main',
                        fontWeight: 'bold',
                        backgroundColor: 'primary.lighter',
                        px: 0.5,
                        borderRadius: 0.5,
                        mx: 0.25
                      }}
                    >
                      {part.content}
                    </Box>
                  );
                } else if (part.type === 'invalid-user-mention') {
                  return (
                    <Box
                      key={index}
                      component="span"
                      sx={{
                        color: 'error.main',
                        fontWeight: 'bold',
                        backgroundColor: 'error.lighter',
                        px: 0.5,
                        borderRadius: 0.5,
                        mx: 0.25,
                        textDecoration: 'line-through'
                      }}
                    >
                      {part.content}
                    </Box>
                  );
                }
                return <span key={index}>{part.content}</span>;
              })}
            </Typography>
          </Box>
        )}
        
                {/* Submit buttons - show for both edit and new message modes */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {onCancel && (
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={onCancel}
              sx={{ minWidth: 100 }}
            >
              Cancel
            </MDButton>
          )}
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleSubmit}
            disabled={!content.trim() || isSubmitting || !!validationError}
            sx={{ minWidth: 100 }}
          >
            {isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : isEditMode ? (
              'Update'
            ) : (
              'Post'
            )}
          </MDButton>
        </Box>
      </Card>

      {/* Mention Popper */}
      <Popper
        open={showMentionMenu}
        anchorEl={mentionAnchor}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
      >
        <Paper 
          ref={popperRef}
          sx={{ 
            mt: 1, 
            maxHeight: 400, 
            overflow: 'auto', 
            minWidth: 350, 
            maxWidth: 500,
            boxShadow: 6,
            borderRadius: 2
          }}
        >
          {!selectedEntityType ? (
            // Show entity types - only User for now
            <>
              <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Select what to mention
                  {mentionSearch && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      Searching: "{mentionSearch}"
                    </Typography>
                  )}
                </Typography>
              </Box>
              <List dense>
                {filteredEntityTypes.length > 0 ? (
                  filteredEntityTypes.map((entity) => (
                    <ListItem
                      button
                      key={entity.type}
                      onClick={() => handleEntityTypeSelect(entity)}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ color: `${entity.color}.main`, minWidth: 40 }}>
                        {entity.icon}
                      </ListItemIcon>
                      <ListItemText primary={entity.label} />
                    </ListItem>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText 
                      primary="No matching entity types"
                      secondary={`No results for "${mentionSearch}"`}
                    />
                  </ListItem>
                )}
              </List>
            </>
          ) : (
            // Show items - only users for now
            <>
              <Box sx={{ 
                p: 1.5, 
                borderBottom: 1, 
                borderColor: 'divider', 
                display: 'flex', 
                alignItems: 'center',
                backgroundColor: 'grey.50'
              }}>
                <ListItemIcon sx={{ color: `${selectedEntityType.color}.main`, minWidth: 40 }}>
                  {selectedEntityType.icon}
                </ListItemIcon>
                <Box>
                  <Typography variant="subtitle2">
                    Select {selectedEntityType.label}
                  </Typography>
                  {mentionSearch && (
                    <Typography variant="caption" color="text.secondary">
                      Searching: "{mentionSearch}"
                    </Typography>
                  )}
                </Box>
              </Box>
              {isLoadingItems ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <CircularProgress size={24} />
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Loading {selectedEntityType.label}...
                  </Typography>
                </Box>
              ) : (
                <List dense sx={{ maxHeight: 300, overflow: 'auto', py: 1 }}>
                  {filteredItems.length > 0 ? (
                    filteredItems.map((item) => (
                      <ListItem
                        button
                        key={item.id}
                        onClick={() => handleItemSelect(item)}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          },
                          py: 2,
                          px: 2,
                          borderRadius: 1,
                          mx: 1,
                          mb: 0.5
                        }}
                      >
                        <MDUserAvatar
                          firstName={item.first_name}
                          middleName={item.middle_name}
                          lastName={item.last_name}
                          avatarUrl={item.avatar}
                          size={36}
                          variant="circular"
                          sx={{ mr: 3, flexShrink: 0 }}
                        />
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 500, ml: 2 }}>
                              {formatEntityName(item, selectedEntityType?.type)}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary" sx={{ml: 2 }}>
                              {selectedEntityType?.type === 'User' && `@${item.username}`}
                              {/* Comment out other entity types
                              {selectedEntityType.type === 'Lead' && item.email}
                              {selectedEntityType.type === 'Deal' && `Stage: ${item.stage || 'N/A'}`}
                              {selectedEntityType.type === 'Account' && item.industry}
                              {selectedEntityType.type === 'Contact' && item.phone}
                              {selectedEntityType.type === 'Order' && `Status: ${item.status || 'N/A'}`}
                              {selectedEntityType.type === 'Invoice' && `Due: ${item.due_date || 'N/A'}`}
                              {selectedEntityType.type === 'Quote' && `Valid until: ${item.valid_until || 'N/A'}`}
                              {selectedEntityType.type === 'Product' && `SKU: ${item.sku || 'N/A'}`}
                              {selectedEntityType.type === 'Case' && `Priority: ${item.priority || 'N/A'}`}
                              */}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText 
                        primary="No items found"
                        secondary={mentionSearch ? `No results for "${mentionSearch}"` : "No items available"}
                      />
                    </ListItem>
                  )}
                </List>
              )}
            </>
          )}
        </Paper>
      </Popper>
    </MDBox>
  );
};

export default EchoComposer;